import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PORTAL, usePortal } from '../portalAuth';
import { openRazorpayCheckout } from '../razorpay';
import { STORE_BASE } from '../routeBase';

const PAY_LABEL = { unpaid: 'On account', pending: 'Payment pending', partial: 'Part paid', full: 'Paid', paid: 'Paid' };
const PAY_COLOR = { unpaid: '#92400e', pending: '#9a6a00', partial: '#1e40af', full: '#166534', paid: '#166534' };
const STATUS_LABEL = {
  processing: 'Processing', packing: 'Packing', ready: 'Ready for dispatch',
  dispatched: 'Dispatched', in_transit: 'In transit', delivered: 'Delivered',
  completed: 'Completed', cancelled: 'Cancelled',
};
const STATUS_COLOR = {
  processing: '#64748b', packing: '#9a6a00', ready: '#1e40af', dispatched: '#7c3aed',
  in_transit: '#7c3aed', delivered: '#166534', completed: '#166534', cancelled: '#991b1b',
};

function fmtDate(d) { return d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'; }
function money(n, cur) { return `${cur || ''} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`.trim(); }

// This is a VIEW-ONLY page — a customer can look up their order and its
// shipment status, nothing here is editable. Order changes (status, tracking,
// approval) only ever happen from the CRM's admin-side Orders screen.
export default function OrderDetail() {
  const { id } = useParams();
  const { customer, ready, refreshCart } = usePortal();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payMsg, setPayMsg] = useState('');
  const [tracking, setTracking] = useState(null);

  useEffect(() => {
    if (!customer) return;
    PORTAL.get(`/orders/${id}`)
      .then(r => setOrder(r.data.order))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [id, customer]);

  useEffect(() => {
    if (!order || !order.shipway_status) return;
    PORTAL.get(`/orders/${id}/tracking`).then(r => setTracking(r.data)).catch(() => {});
  }, [order, id]);

  const reorder = async () => {
    setReordering(true);
    try {
      const r = await PORTAL.post(`/orders/${id}/reorder`);
      toast.success(r.data.message);
      refreshCart();
      nav(`${STORE_BASE}/cart`);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not reorder'); }
    setReordering(false);
  };

  // Re-opens Razorpay for an order that's still 'pending' — most often
  // because the customer closed the modal at checkout without paying.
  const payNow = async () => {
    setPaying(true);
    setPayMsg('');
    try {
      const r = await PORTAL.post(`/order/${id}/razorpay/retry`);
      setPaying(false);
      openRazorpayCheckout(id, r.data, {
        onVerified: (payment_status) => {
          toast.success('Payment received — thank you!');
          setOrder(o => ({ ...o, payment_status, payment_received: o.total_amount }));
        },
        onPending: (reason) => {
          setPayMsg(reason === 'dismissed' ? 'Payment window closed — you can try again anytime.' : 'Payment did not complete — you can try again anytime.');
        },
      });
    } catch (e) { toast.error(e.response?.data?.error || 'Could not start payment'); setPaying(false); }
  };

  const requestInvoice = async () => {
    setRequesting(true);
    try {
      const r = await PORTAL.post('/request-document', { doc_type: 'invoice', reference: order.order_number });
      toast.success(r.data.message);
    } catch (e) { toast.error(e.response?.data?.error || 'Could not send request'); }
    setRequesting(false);
  };

  if (!ready || loading) return <div style={{ padding: 60, textAlign: 'center', color: '#aaa' }}>Loading…</div>;
  if (!customer) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Please sign in to view this order</h2>
      <Link to={`${STORE_BASE}/login`} style={{ color: 'var(--sf-accent)' }}>Sign in</Link>
    </div>
  );
  if (notFound || !order) return (
    <div style={{ maxWidth: 500, margin: '60px auto', textAlign: 'center' }}>
      <h2>Order not found</h2>
      <Link to={`${STORE_BASE}/orders`} style={{ color: 'var(--sf-accent)' }}>← Back to My Orders</Link>
    </div>
  );

  const cur = customer.currency;
  const hasShipment = order.status === 'dispatched' || order.status === 'in_transit' || order.status === 'delivered' || (order.awb_bl_number && order.awb_bl_number !== '');
  const hasShipAddress = order.ship_address || order.ship_city;

  return (
    <div style={{ maxWidth: 820, margin: '24px auto', padding: '0 24px 60px' }}>
      <Link to={`${STORE_BASE}/orders`} style={{ fontSize: 13, color: 'var(--sf-accent)', textDecoration: 'none' }}>← Back to My Orders</Link>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', margin: '14px 0 20px', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{order.order_number}</h1>
          <div style={{ fontSize: 13, color: '#888', marginTop: 4 }}>Placed on {fmtDate(order.created_at)}</div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 14,
            background: `${STATUS_COLOR[order.status] || '#666'}18`, color: STATUS_COLOR[order.status] || '#666' }}>
            {STATUS_LABEL[order.status] || order.status}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 14, background: '#fff7ed', color: PAY_COLOR[order.payment_status] || '#666' }}>
            {PAY_LABEL[order.payment_status] || order.payment_status}
          </span>
          {order.approval_status === 'pending' && (
            <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 14, background: '#fef3c7', color: '#92400e' }}>Awaiting approval</span>
          )}
          {order.approval_status === 'rejected' && (
            <span style={{ fontSize: 12, fontWeight: 700, padding: '5px 12px', borderRadius: 14, background: '#fee2e2', color: '#991b1b' }}>Rejected</span>
          )}
        </div>
      </div>

      {order.approval_status === 'rejected' && order.rejection_reason && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
          <strong>This order needs attention:</strong> {order.rejection_reason}
        </div>
      )}

      {hasShipment && (
        <Section title="Shipment Tracking">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, fontSize: 13 }}>
            {order.awb_bl_number && <Info label="AWB / BL Number" value={order.awb_bl_number} />}
            {order.shipment_mode && <Info label="Shipment Mode" value={order.shipment_mode} capitalize />}
            {order.dispatch_date && <Info label="Dispatched" value={fmtDate(order.dispatch_date)} />}
            {order.eta_date && <Info label="Estimated Arrival" value={fmtDate(order.eta_date)} />}
            {order.delivered_date && <Info label="Delivered" value={fmtDate(order.delivered_date)} />}
          </div>
          {order.tracking_url ? (
            <a href={order.tracking_url} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: 'var(--sf-accent)', display: 'inline-block', marginTop: 12 }}>
              📍 Track shipment →
            </a>
          ) : order.awb_bl_number && (
            <a href={`https://www.google.com/search?q=${encodeURIComponent(order.awb_bl_number + ' tracking')}`} target="_blank" rel="noreferrer"
              style={{ fontSize: 13, color: 'var(--sf-accent)', display: 'inline-block', marginTop: 12 }}>
              🔍 Search tracking →
            </a>
          )}

          {tracking?.tracked && (
            <div style={{ marginTop: 16, borderTop: '1px solid #f2f2f2', paddingTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                {tracking.carrier ? `${tracking.carrier} — ` : ''}{tracking.status_label || tracking.status || 'Tracking'}
              </div>
              {Array.isArray(tracking.scan) && tracking.scan.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {tracking.scan.slice(0, 6).map((s, i) => (
                    <div key={i} style={{ fontSize: 12, color: '#666', display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                      <span>{s.status_detail}{s.location ? ` — ${s.location}` : ''}</span>
                      <span style={{ color: '#999', whiteSpace: 'nowrap' }}>{s.time}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </Section>
      )}

      {hasShipAddress && (
        <Section title="Delivery Address">
          <div style={{ fontSize: 13.5, lineHeight: 1.7, color: '#444' }}>
            {order.ship_address && <div>{order.ship_address}</div>}
            <div>{[order.ship_city, order.ship_state, order.ship_postal_code].filter(Boolean).join(', ')}</div>
            {order.ship_country && <div>{order.ship_country}</div>}
            {order.ship_gstin && <div style={{ marginTop: 4, color: '#888' }}>GSTIN: {order.ship_gstin}</div>}
          </div>
        </Section>
      )}

      <Section title={`Items (${order.items.length})`}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, minWidth: 480 }}>
            <thead>
              <tr style={{ textAlign: 'left', color: '#999', fontSize: 11.5 }}>
                <th style={{ padding: '6px 4px' }}>Model</th>
                <th style={{ padding: '6px 4px' }}>Color</th>
                <th style={{ padding: '6px 4px' }}>Size</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Qty</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Unit Price</th>
                <th style={{ padding: '6px 4px', textAlign: 'right' }}>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map(it => (
                <tr key={it.id} style={{ borderTop: '1px solid #f2f2f2' }}>
                  <td style={{ padding: '8px 4px', fontWeight: 600 }}>{it.model_no}</td>
                  <td style={{ padding: '8px 4px', color: '#666' }}>{it.color || '—'}</td>
                  <td style={{ padding: '8px 4px', color: '#666' }}>{it.size || '—'}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{it.qty}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right' }}>{money(it.unit_price, cur)}</td>
                  <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700 }}>{money(it.line_total, cur)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ borderTop: '1px solid #eee', marginTop: 14, paddingTop: 14, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
          {order.discount_amount > 0 && (
            <div style={{ fontSize: 13, color: '#666' }}>Discount: <strong>-{money(order.discount_amount, cur)}</strong></div>
          )}
          <div style={{ fontSize: 15, fontWeight: 700 }}>Total: {money(order.total_amount, cur)}</div>
          {order.payment_received > 0 && (
            <div style={{ fontSize: 13, color: '#166534' }}>Payment received: {money(order.payment_received, cur)}</div>
          )}
        </div>
      </Section>

      {order.notes && (
        <Section title="Notes">
          <div style={{ fontSize: 13, color: '#666', whiteSpace: 'pre-wrap' }}>{order.notes}</div>
        </Section>
      )}

      {order.gateway === 'razorpay' && order.payment_status === 'pending' && (
        <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 13.5 }}>Payment pending</div>
            <div style={{ fontSize: 12.5, color: '#555', marginTop: 2 }}>{payMsg || 'This order hasn’t been paid yet.'}</div>
          </div>
          <button onClick={payNow} disabled={paying}
            style={{ padding: '9px 20px', border: 'none', background: 'var(--sf-accent)', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
            {paying ? 'Starting…' : '💳 Pay Now'}
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 20 }}>
        <button onClick={reorder} disabled={reordering} className="sf-btn-outline"
          style={{ padding: '9px 20px', border: '1.5px solid var(--sf-accent)', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          {reordering ? 'Adding to cart…' : '↻ Reorder'}
        </button>
        <button onClick={requestInvoice} disabled={requesting}
          style={{ padding: '9px 20px', border: '1.5px solid #ddd', background: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          {requesting ? 'Sending…' : '📄 Request Invoice Copy'}
        </button>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 18, marginBottom: 16 }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, margin: '0 0 12px' }}>{title}</h3>
      {children}
    </div>
  );
}
function Info({ label, value, capitalize }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#999', marginBottom: 2 }}>{label}</div>
      <div style={{ fontWeight: 600, textTransform: capitalize ? 'capitalize' : 'none' }}>{value}</div>
    </div>
  );
}
